import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import {
  Settings,
  Upload,
  Image,
  FileText,
  Save,
  RefreshCw,
  Globe,
  Building2,
  Mail,
  Phone,
  MapPin,
  Palette,
  Check } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
"@/shared/components/ui/select";

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const DEFAULT_SETTINGS = {
  company_name: 'Demo Company',
  company_logo_url: '',
  company_address: '123 Business Street, São Paulo, SP 01310-100',
  company_phone: '+55 11 1234-5678',
  company_email: 'finance@company.com',
  company_website: 'www.company.com',
  company_cnpj: '12.345.678/0001-90',
  pdf_header_color: '#1e3a5f',
  pdf_accent_color: '#10b981',
  pdf_show_logo: true,
  pdf_show_watermark: false,
  pdf_paper_size: 'A4',
  pdf_include_page_numbers: true,
  currency_api_provider: 'exchangerate',
  currency_api_key: '',
  currency_auto_refresh: true,
  currency_refresh_interval: 60,
  default_currency: 'USD'
};

const CURRENCY_PROVIDERS = [
{ value: 'exchangerate', label: 'ExchangeRate-API (Free)', free: true },
{ value: 'openexchange', label: 'Open Exchange Rates', free: false },
{ value: 'currencylayer', label: 'CurrencyLayer', free: false },
{ value: 'fixer', label: 'Fixer.io', free: false }];


export const DocumentSetup = () => {const { t } = useTranslation("finance");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [previewPDF, setPreviewPDF] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/settings/document-setup`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSettings({ ...DEFAULT_SETTINGS, ...data });
      }
    } catch (err) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/finance/settings/document-setup`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Settings saved successfully!');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Convert to base64 for demo - in production, upload to storage
      const reader = new FileReader();
      reader.onloadend = () => {
        setSettings({ ...settings, company_logo_url: reader.result });
        toast.success('Logo uploaded');
      };
      reader.readAsDataURL(file);
    }
  };

  const testCurrencyAPI = async () => {
    try {
      const endpoint = settings.currency_api_provider === 'exchangerate' ?
      `${API_URL}/api/finance/currency/rates-live?base_currency=USD` :
      `${API_URL}/api/finance/currency/rates?base_currency=USD`;

      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Currency API working! Got ${Object.keys(data.rates || {}).length} rates from ${data.source || 'API'}`);
      } else {
        toast.error('Currency API test failed');
      }
    } catch (err) {
      toast.error('Failed to test currency API');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="document-setup">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("documentSetup", "Document Setup")}</h1>
          <p className="text-muted-foreground mt-1">{t("configureCompanyInformation", "Configure company information, PDF styling, and currency API settings")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("reset", "Reset")}

          </Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="company" className="space-y-4">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="company" className="gap-2">
            <Building2 className="h-4 w-4" />{t("companyInfo", "Company Info")}

          </TabsTrigger>
          <TabsTrigger value="pdf" className="gap-2">
            <FileText className="h-4 w-4" />{t("pdfSettings", "PDF Settings")}

          </TabsTrigger>
          <TabsTrigger value="currency" className="gap-2">
            <Globe className="h-4 w-4" />{t("currencyApi", "Currency API")}

          </TabsTrigger>
        </TabsList>

        {/* Company Info Tab */}
        <TabsContent value="company" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Logo Upload */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="h-5 w-5" />{t("companyLogo", "Company Logo")}

                </CardTitle>
                <CardDescription>{t("uploadYourCompany", "Upload your company logo for use in PDF reports")}

                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center bg-muted/50 overflow-hidden">
                    {settings.company_logo_url ?
                    <img
                      src={settings.company_logo_url}
                      alt="Company Logo"
                      className="max-w-full max-h-full object-contain" /> :


                    <div className="text-center text-muted-foreground text-sm">
                        <Image className="h-8 w-8 mx-auto mb-2 opacity-50" />{t("noLogo", "No logo")}

                    </div>
                    }
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload" />
                    
                    <label htmlFor="logo-upload">
                      <Button variant="outline" className="gap-2" asChild>
                        <span>
                          <Upload className="h-4 w-4" />{t("uploadLogo", "Upload Logo")}

                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">{t("recommended200x200pxPng", "Recommended: 200x200px, PNG or JPG")}

                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />{t("companyDetails", "Company Details")}

                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t("companyName", "Company Name")}</label>
                  <input
                    type="text"
                    value={settings.company_name}
                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md" />
                  
                </div>
                <div>
                  <label className="text-sm font-medium">{t("cnpj", "CNPJ")}</label>
                  <input
                    type="text"
                    value={settings.company_cnpj}
                    onChange={(e) => setSettings({ ...settings, company_cnpj: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                    placeholder="00.000.000/0001-00" />
                  
                </div>
                <div>
                  <label className="text-sm font-medium">{t("website", "Website")}</label>
                  <input
                    type="text"
                    value={settings.company_website}
                    onChange={(e) => setSettings({ ...settings, company_website: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                    placeholder="www.company.com" />
                  
                </div>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t("contactInformation", "Contact Information")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2">
                      <MapPin className="h-4 w-4" />{t("address", "Address")}
                    </label>
                    <textarea
                      value={settings.company_address}
                      onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border rounded-md h-20" />
                    
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4" />{t("phone", "Phone")}
                    </label>
                    <input
                      type="tel"
                      value={settings.company_phone}
                      onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border rounded-md" />
                    
                  </div>
                  <div>
                    <label className="text-sm font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4" />{t("email", "Email")}
                    </label>
                    <input
                      type="email"
                      value={settings.company_email}
                      onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border rounded-md" />
                    
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* PDF Settings Tab */}
        <TabsContent value="pdf" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* PDF Styling */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />{t("pdfStyling", "PDF Styling")}

                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t("headerColor", "Header Color")}</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="color"
                      value={settings.pdf_header_color}
                      onChange={(e) => setSettings({ ...settings, pdf_header_color: e.target.value })}
                      className="w-12 h-10 border rounded cursor-pointer" />
                    
                    <input
                      type="text"
                      value={settings.pdf_header_color}
                      onChange={(e) => setSettings({ ...settings, pdf_header_color: e.target.value })}
                      className="flex-1 px-3 py-2 border rounded-md" />
                    
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("accentColor", "Accent Color")}</label>
                  <div className="flex items-center gap-3 mt-1">
                    <input
                      type="color"
                      value={settings.pdf_accent_color}
                      onChange={(e) => setSettings({ ...settings, pdf_accent_color: e.target.value })}
                      className="w-12 h-10 border rounded cursor-pointer" />
                    
                    <input
                      type="text"
                      value={settings.pdf_accent_color}
                      onChange={(e) => setSettings({ ...settings, pdf_accent_color: e.target.value })}
                      className="flex-1 px-3 py-2 border rounded-md" />
                    
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("paperSize", "Paper Size")}</label>
                  <Select
                    value={settings.pdf_paper_size}
                    onValueChange={(v) => setSettings({ ...settings, pdf_paper_size: v })}>
                    
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="A4">{t("a4210297", "A4 (210 \xD7 297 mm)")}</SelectItem>
                      <SelectItem value="Letter">{t("letter8511", "Letter (8.5 \xD7 11 in)")}</SelectItem>
                      <SelectItem value="Legal">{t("legal8514", "Legal (8.5 \xD7 14 in)")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* PDF Options */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />{t("pdfOptions", "PDF Options")}

                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.pdf_show_logo}
                    onChange={(e) => setSettings({ ...settings, pdf_show_logo: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300" />
                  
                  <span>{t("showCompanyLogo", "Show company logo in PDF header")}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.pdf_include_page_numbers}
                    onChange={(e) => setSettings({ ...settings, pdf_include_page_numbers: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300" />
                  
                  <span>{t("includePageNumbers", "Include page numbers")}</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.pdf_show_watermark}
                    onChange={(e) => setSettings({ ...settings, pdf_show_watermark: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300" />
                  
                  <span>{t("showConfidentialWatermark", "Show \"CONFIDENTIAL\" watermark")}</span>
                </label>
              </CardContent>
            </Card>

            {/* PDF Preview */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t("pdfPreview", "PDF Preview")}</CardTitle>
                <CardDescription>{t("previewHowYour", "Preview how your PDF documents will look")}

                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className="border rounded-lg p-6 bg-white shadow-inner"
                  style={{ minHeight: '300px' }}>
                  
                  {/* Simulated PDF Header */}
                  <div
                    className="p-4 rounded-t-lg text-white mb-4"
                    style={{ backgroundColor: settings.pdf_header_color }}>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {settings.pdf_show_logo && settings.company_logo_url &&
                        <img
                          src={settings.company_logo_url}
                          alt="Logo"
                          className="h-12 w-12 object-contain bg-white rounded p-1" />

                        }
                        <div>
                          <h3 className="text-lg font-bold">{settings.company_name}</h3>
                          <p className="text-sm opacity-80">{t("cashFlowStatement", "Cash Flow Statement")}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm opacity-80">
                        <p>{new Date().toLocaleDateString()}</p>
                        <p>{t("periodMonthly", "Period: Monthly")}</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Simulated Content */}
                  <div className="space-y-3">
                    <div
                      className="h-3 rounded w-full"
                      style={{ backgroundColor: settings.pdf_accent_color + '40' }} />
                    
                    <div className="h-2 bg-gray-200 rounded w-3/4" />
                    <div className="h-2 bg-gray-200 rounded w-1/2" />
                    <div className="h-2 bg-gray-200 rounded w-2/3" />
                    <div
                      className="h-3 rounded w-full mt-4"
                      style={{ backgroundColor: settings.pdf_accent_color + '40' }} />
                    
                    <div className="h-2 bg-gray-200 rounded w-5/6" />
                    <div className="h-2 bg-gray-200 rounded w-3/5" />
                  </div>

                  {/* Footer */}
                  <div className="mt-6 pt-4 border-t text-xs text-gray-500 flex justify-between">
                    <span>{settings.company_email} | {settings.company_phone}</span>
                    {settings.pdf_include_page_numbers && <span>{t("page1Of", "Page 1 of 2")}</span>}
                  </div>

                  {/* Watermark */}
                  {settings.pdf_show_watermark &&
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-10">
                      <span className="text-4xl font-bold text-gray-600 rotate-[-30deg]">{t("confidential", "CONFIDENTIAL")}

                    </span>
                    </div>
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Currency API Tab */}
        <TabsContent value="currency" className="space-y-4">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* API Provider */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />{t("currencyApiProvider", "Currency API Provider")}

                </CardTitle>
                <CardDescription>{t("selectYourPreferred", "Select your preferred currency exchange rate provider")}

                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t("provider", "Provider")}</label>
                  <Select
                    value={settings.currency_api_provider}
                    onValueChange={(v) => setSettings({ ...settings, currency_api_provider: v })}>
                    
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_PROVIDERS.map((p) =>
                      <SelectItem key={p.value} value={p.value}>
                          <div className="flex items-center gap-2">
                            {p.label}
                            {p.free && <Badge variant="secondary" className="text-xs">{t("free", "Free")}</Badge>}
                          </div>
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {settings.currency_api_provider !== 'exchangerate' &&
                <div>
                    <label className="text-sm font-medium">{t("apiKey", "API Key")}</label>
                    <input
                    type="password"
                    value={settings.currency_api_key}
                    onChange={(e) => setSettings({ ...settings, currency_api_key: e.target.value })}
                    placeholder="Enter your API key"
                    className="w-full mt-1 px-3 py-2 border rounded-md" />
                  
                    <p className="text-xs text-muted-foreground mt-1">{t("getYourApi", "Get your API key from the provider's dashboard")}

                  </p>
                  </div>
                }

                <Button variant="outline" onClick={testCurrencyAPI} className="gap-2">
                  <Check className="h-4 w-4" />{t("testConnection", "Test Connection")}

                </Button>
              </CardContent>
            </Card>

            {/* Currency Settings */}
            <Card>
              <CardHeader>
                <CardTitle>{t("currencySettings", "Currency Settings")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">{t("defaultCurrency", "Default Currency")}</label>
                  <Select
                    value={settings.default_currency}
                    onValueChange={(v) => setSettings({ ...settings, default_currency: v })}>
                    
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">{t("usdUsDollar", "USD - US Dollar")}</SelectItem>
                      <SelectItem value="EUR">{t("eurEuro", "EUR - Euro")}</SelectItem>
                      <SelectItem value="GBP">{t("gbpBritishPound", "GBP - British Pound")}</SelectItem>
                      <SelectItem value="BRL">{t("brlBrazilianReal", "BRL - Brazilian Real")}</SelectItem>
                      <SelectItem value="CAD">{t("cadCanadianDollar", "CAD - Canadian Dollar")}</SelectItem>
                      <SelectItem value="JPY">{t("jpyJapaneseYen", "JPY - Japanese Yen")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.currency_auto_refresh}
                    onChange={(e) => setSettings({ ...settings, currency_auto_refresh: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300" />
                  
                  <span>{t("autorefreshExchangeRates", "Auto-refresh exchange rates")}</span>
                </label>

                {settings.currency_auto_refresh &&
                <div>
                    <label className="text-sm font-medium">{t("refreshIntervalMinutes", "Refresh Interval (minutes)")}</label>
                    <Select
                    value={settings.currency_refresh_interval.toString()}
                    onValueChange={(v) => setSettings({ ...settings, currency_refresh_interval: parseInt(v) })}>
                    
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">{t("every15Minutes", "Every 15 minutes")}</SelectItem>
                        <SelectItem value="30">{t("every30Minutes", "Every 30 minutes")}</SelectItem>
                        <SelectItem value="60">{t("everyHour", "Every hour")}</SelectItem>
                        <SelectItem value="360">{t("every6Hours", "Every 6 hours")}</SelectItem>
                        <SelectItem value="1440">{t("onceDaily", "Once daily")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                }
              </CardContent>
            </Card>

            {/* Provider Info */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>{t("providerInformation", "Provider Information")}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  {CURRENCY_PROVIDERS.map((p) =>
                  <div
                    key={p.value}
                    className={`p-4 border rounded-lg ${settings.currency_api_provider === p.value ? 'border-emerald-500 bg-emerald-50' : ''}`}>
                    
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{p.label}</span>
                        {p.free ?
                      <Badge className="bg-emerald-100 text-emerald-700">{t("free", "Free")}</Badge> :

                      <Badge variant="secondary">{t("paid", "Paid")}</Badge>
                      }
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {p.value === 'exchangerate' && 'Free tier: 1,500 requests/month. Great for small businesses.'}
                        {p.value === 'openexchange' && 'Starting at $12/month. 1,000+ currencies supported.'}
                        {p.value === 'currencylayer' && 'Starting at $9.99/month. Real-time rates, 168 currencies.'}
                        {p.value === 'fixer' && 'Starting at €9.99/month. European Central Bank rates.'}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>);

};

export default DocumentSetup;